import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Props {
  titre?: string;
  contenu: string;
}

/**
 * Rendu d'une production au format document.
 *
 * Les assistants du PEV produisent du Markdown structuré : titres, tableaux,
 * citations, listes. Sans rendu, l'utilisateur voit du JSON brut — ce qui rend
 * une production par ailleurs correcte parfaitement inutilisable.
 *
 * Les styles sont posés élément par élément plutôt que par la classe `prose` de
 * Tailwind Typography, qui n'est pas installée sur ce projet.
 */
const RenduDocument: React.FC<Props> = ({ titre, contenu }) => (
  <div className="space-y-4">
    {titre && <h2 className="text-xl font-semibold text-eg-encre">{titre}</h2>}

    <div className="text-[15px] leading-relaxed text-eg-encre">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        // Les types de react-markdown 8 attendent des props spécifiques par
        // balise (level pour les titres, href pour les liens). On ne se sert
        // que des enfants : le cast évite d'avoir à tous les déclarer.
        components={{
          h1: ({ children }: any) => (
            <h1 className="mt-6 mb-3 border-b border-eg-bord pb-2 text-2xl font-bold text-eg-encre">{children}</h1>
          ),
          h2: ({ children }: any) => (
            <h2 className="mt-6 mb-2 text-lg font-semibold text-eg-encre">{children}</h2>
          ),
          h3: ({ children }: any) => (
            <h3 className="mt-4 mb-2 text-base font-semibold text-eg-encre">{children}</h3>
          ),
          p: ({ children }: any) => <p className="my-3">{children}</p>,
          ul: ({ children }: any) => <ul className="my-3 list-disc space-y-1 pl-6">{children}</ul>,
          ol: ({ children }: any) => <ol className="my-3 list-decimal space-y-1 pl-6">{children}</ol>,
          li: ({ children }: any) => <li className="pl-1">{children}</li>,
          strong: ({ children }: any) => <strong className="font-semibold text-eg-encre">{children}</strong>,
          blockquote: ({ children }: any) => (
            <blockquote className="my-4 border-l-4 border-amber-300 bg-eg-alerte/10 px-4 py-2 text-eg-brun">
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-6 border-eg-bord" />,
          a: ({ href, children }: any) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-eg-accent underline">
              {children}
            </a>
          ),
          // Les tableaux portent l'essentiel de l'information dans ces documents :
          // ils doivent défiler horizontalement plutôt que déborder de la page.
          table: ({ children }: any) => (
            <div className="my-4 overflow-x-auto rounded-md border border-eg-bord">
              <table className="w-full border-collapse text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }: any) => <thead className="bg-eg-papier">{children}</thead>,
          th: ({ children }: any) => (
            <th className="border-b border-eg-bord px-3 py-2 text-left font-semibold text-eg-brun">{children}</th>
          ),
          td: ({ children }: any) => (
            <td className="border-b border-eg-bordure px-3 py-2 align-top">{children}</td>
          ),
          code: ({ children }: any) => (
            <code className="rounded bg-eg-voile px-1.5 py-0.5 font-mono text-[13px] text-eg-encre">
              {children}
            </code>
          ),
        }}
      >
        {contenu}
      </ReactMarkdown>
    </div>
  </div>
);

export default RenduDocument;
